import type {
  AuditPage, AuthCaptchaConfig, AuthResponse, CompanyDto, EmbedConfigDto, FormAnalytics, FormDefinition,
  FormDetail, FormListPage, InviteDto, NotificationDto, PublishResult, ResponseRow,
  InvitationDto, MfaStatusDto, RoleDto, SaveDefinitionResult, ShareLinkDto,
  TeamMemberDto, TenantMembershipDto, ThemeDto, ThemePreviewDto, ThemeTokensDto,
  AutoTranslateDto, ProjectDto, TranslationsDto,
  ApiKeyDto, ApiKeyCreatedDto, RollupStatusDto, SessionDto,
  InboxPage, InboxQuery, ResponseDetail, FormDashboard, OrganisationDto, OrganisationBranding,
  ValidationIssue, WorkspaceDto,
} from './types';

const TOKEN_KEY = 'ff.auth';

export interface StoredAuth {
  accessToken: string;
  refreshToken: string;
  expiresUtc: string;
  user: AuthResponse['user'];
  tenant: AuthResponse['tenant'];
}

export const authStore = {
  read(): StoredAuth | null {
    try {
      const raw = localStorage.getItem(TOKEN_KEY);
      return raw ? (JSON.parse(raw) as StoredAuth) : null;
    } catch {
      // Private windows and cleared site data both land here; treat as signed out.
      return null;
    }
  },
  write(auth: StoredAuth) {
    try { localStorage.setItem(TOKEN_KEY, JSON.stringify(auth)); } catch { /* ignore */ }
  },
  clear() {
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
  },
};

/** RFC 9457 problem details, with the per-field breakdown the API returns for 422s. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly issues: ValidationIssue[] = [],
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isValidation() { return this.status === 422; }
  get isAuth() { return this.status === 401; }
}

interface ProblemDetails {
  title?: string;
  detail?: string;
  code?: string;
  requestId?: string;
  errors?: { questionRef?: string; path?: string; code: string; message: string }[];
}

let refreshInFlight: Promise<boolean> | null = null;

async function refreshToken(): Promise<boolean> {
  const auth = authStore.read();
  if (!auth?.refreshToken) return false;

  // Refresh tokens are single-use: two concurrent 401s must not both spend one, or the
  // second is treated as a stolen-token reuse and kills the whole family (docs/10 §5).
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch('/v1/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: auth.refreshToken }),
      });
      if (!res.ok) { authStore.clear(); return false; }

      const data = (await res.json()) as
        { accessToken: string; refreshToken: string; expiresUtc: string };
      authStore.write({ ...auth, ...data });
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  retry = true,
): Promise<T> {
  const auth = authStore.read();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (auth?.accessToken) headers.Authorization = `Bearer ${auth.accessToken}`;

  const res = await fetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (res.status === 401 && retry && auth?.refreshToken) {
    if (await refreshToken()) return request<T>(method, path, body, false);
  }

  if (!res.ok) {
    let problem: ProblemDetails = {};
    try { problem = (await res.json()) as ProblemDetails; } catch { /* non-JSON body */ }

    throw new ApiError(
      res.status,
      problem.code ?? 'request_failed',
      problem.detail ?? problem.title ?? `Request failed (${res.status})`,
      (problem.errors ?? []).map((e) => ({
        path: e.path ?? e.questionRef ?? '',
        code: e.code,
        message: e.message,
      })),
      problem.requestId,
    );
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/**
 * A date filter on the feed is a day in the viewer's own timezone. That is what a date
 * input produces, what the URL carries, and what somebody means by "today" — but it is not
 * what a bare date means to the server, which can only read one as a UTC day. The two
 * differ by the viewer's offset, so in Delhi a bare "today" would hide everything
 * submitted before 05:30 and show the last five and a half hours of yesterday instead.
 *
 * So the day is turned into the pair of instants that bracket it here, once, rather than
 * at each call site. Sending an explicit instant is also the only unambiguous form: the
 * bare-date shorthand stays a UTC day for API clients, who have no viewer to speak of.
 */
const LocalDay = /^\d{4}-\d{2}-\d{2}$/;

/** Only ever called on a string that matched `LocalDay`, so the slices are whole numbers. */
function dayStart(day: string, plusDays = 0) {
  const year = Number(day.slice(0, 4));
  const month = Number(day.slice(5, 7));
  const date = Number(day.slice(8, 10));
  // Day overflow normalises, so the last of a month needs no special case.
  return new Date(year, month - 1, date + plusDays).toISOString();
}

export const api = {
  auth: {
    register: (input: {
      email: string; password: string; displayName?: string; organisationName?: string;
      captchaToken?: string;
    }) => request<AuthResponse>('POST', '/v1/auth/register', input),

    login: (input: {
      email: string; password: string; mfaCode?: string; captchaToken?: string;
    }) => request<AuthResponse>('POST', '/v1/auth/login', input),

    /** Anonymous: it has to answer before there is anything to sign in with. */
    captcha: () => request<AuthCaptchaConfig>('GET', '/v1/auth/captcha'),

    me: () => request<{
      publicId: string; email: string; emailVerifiedUtc?: string | null;
      displayName?: string; tenant: string; permissions: string[];
    }>('GET', '/v1/auth/me'),

    /**
     * Always resolves with the same message whether or not the address has an account.
     * The caller must not branch on it — showing "no such account" for one address and
     * "check your inbox" for another is the enumeration this endpoint exists to avoid.
     */
    forgotPassword: (input: { email: string; captchaToken?: string }) =>
      request<{ message: string }>('POST', '/v1/auth/forgot-password', input),

    resetPassword: (input: { token: string; password: string }) =>
      request<{ reset: boolean }>('POST', '/v1/auth/reset-password', input),

    verifyEmail: (input: { token: string }) =>
      request<{ verified: boolean; email: string }>('POST', '/v1/auth/verify-email', input),

    /**
     * The sign-in methods this server offers, for the buttons on the sign-in page.
     *
     * Anonymous, and deliberately says only what a button needs: a name and an address.
     * Nothing here identifies a tenant, because the page is reached before anybody has
     * said which one they are heading for.
     */
    ssoConnections: () =>
      request<{ data: { slug: string; displayName: string; provider: string }[] }>(
        'GET', '/v1/auth/sso/connections'),

    /**
     * Trades the one-time code in the return URL for real tokens.
     *
     * A POST, so what comes back lands in a response body rather than in browser history,
     * a referrer header, or a proxy log — which is the whole reason the tokens were not
     * put in the redirect in the first place.
     */
    ssoExchange: (input: { code: string }) =>
      request<AuthResponse>('POST', '/v1/auth/sso/exchange', input),
  },

  forms: {
    list: (params?: {
      status?: string; type?: string; q?: string; companyId?: string; projectId?: string;
      page?: number; pageSize?: number;
    }) => {
      const search = new URLSearchParams();
      if (params?.status) search.set('status', params.status);
      if (params?.type) search.set('type', params.type);
      if (params?.q) search.set('q', params.q);
      if (params?.companyId) search.set('companyId', params.companyId);
      // "none" is a filter for what has not been filed yet, not an absent parameter.
      if (params?.projectId) search.set('projectId', params.projectId);
      if (params?.page) search.set('page', String(params.page));
      if (params?.pageSize) search.set('pageSize', String(params.pageSize));
      const qs = search.toString();
      return request<FormListPage>('GET', `/v1/forms${qs ? `?${qs}` : ''}`);
    },

    create: (input: {
      name: string; description?: string; type: number; workspaceId?: string;
    }) =>
      request<{ id: string; name: string; type: string; status: string; draftVersionId: string }>(
        'POST', '/v1/forms', input),

    get: (formId: string) => request<FormDetail>('GET', `/v1/forms/${formId}`),

    rename: (formId: string, input: { name?: string; description?: string }) =>
      request<{ id: string; name: string; description?: string }>(
        'PATCH', `/v1/forms/${formId}`, input),

    // Soft delete: the responses and audit trail underneath survive, so this is
    // "nobody can reach it" rather than "the evidence is gone".
    remove: (formId: string) => request<void>('DELETE', `/v1/forms/${formId}`),

    duplicate: (formId: string, input?: { name?: string }) =>
      request<{ id: string; name: string; status: string }>(
        'POST', `/v1/forms/${formId}/duplicate`, input ?? {}),

    getDefinition: (formId: string) =>
      request<FormDefinition>('GET', `/v1/forms/${formId}/definition`),

    saveDefinition: (formId: string, definition: FormDefinition) =>
      request<SaveDefinitionResult>('PUT', `/v1/forms/${formId}/definition`, definition),

    validate: (formId: string) =>
      request<{ valid: boolean; errors: ValidationIssue[]; warnings: ValidationIssue[] }>(
        'POST', `/v1/forms/${formId}/validate`),

    publish: (formId: string, changeSummary?: string) =>
      request<PublishResult>('POST', `/v1/forms/${formId}/publish`, { changeSummary }),

    close: (formId: string) =>
      request<{ id: string; status: string }>('POST', `/v1/forms/${formId}/close`),

    versions: (formId: string) => request<{
      data: { id: string; number: number; status: string; questionCount: number;
              changeSummary?: string; publishedUtc?: string; createdUtc: string }[];
    }>('GET', `/v1/forms/${formId}/versions`),
  },

  responses: {
    list: (formId: string, cursor?: string) =>
      request<{ data: ResponseRow[]; hasMore: boolean; nextCursor?: string }>(
        'GET', `/v1/forms/${formId}/responses${cursor ? `?cursor=${cursor}` : ''}`),

    summary: (formId: string) =>
      request<FormAnalytics>('GET', `/v1/forms/${formId}/summary`),

    remove: (responseId: string) =>
      request<void>('DELETE', `/v1/responses/${responseId}`),

    csvUrl: (formId: string) => `/v1/forms/${formId}/responses.csv`,

    /**
     * The generated dashboard (docs/25). Separate from `summary`, which keeps its shape
     * because the contract test and anything already integrating depend on it.
     *
     * Dates are whole days. That is the difference between summing rollup rows and
     * scanning every answer the form has ever collected.
     */
    dashboard: (formId: string, range?: { from?: string; to?: string }) => {
      const search = new URLSearchParams();
      if (range?.from) search.set('from', range.from);
      if (range?.to) search.set('to', range.to);
      const qs = search.toString();
      return request<FormDashboard>(
        'GET', `/v1/forms/${formId}/dashboard${qs ? `?${qs}` : ''}`);
    },
  },

  /**
   * Responses without a form in the path.
   *
   * Kept beside `responses` rather than inside it because the two answer different
   * questions — "how is this form doing" against "what came in" — and callers of one have
   * no use for the other's parameters.
   */
  inbox: {
    list: (params: InboxQuery = {}) => {
      const search = new URLSearchParams();

      for (const [key, value] of Object.entries(params)) {
        // A blank filter is an absent one. Sending `status=` would be read by the server
        // as a filter it cannot parse rather than as no filter at all.
        if (value === undefined || value === null || value === '' || value === false) continue;

        if ((key === 'from' || key === 'to') && LocalDay.test(String(value))) {
          // `to` names the last day wanted, and the server's upper bound is exclusive, so
          // what goes on the wire is the start of the day after it.
          search.set(key, dayStart(String(value), key === 'to' ? 1 : 0));
          continue;
        }

        search.set(key, String(value));
      }

      const qs = search.toString();
      return request<InboxPage>('GET', `/v1/responses${qs ? `?${qs}` : ''}`);
    },

    // `reveal` is a request to disclose personal data and is audited server-side, so it is
    // never sent speculatively — only when somebody has actually asked to see it.
    detail: (responseId: string, reveal = false) =>
      request<ResponseDetail>(
        'GET', `/v1/responses/${responseId}/detail${reveal ? '?reveal=true' : ''}`),

    pdfUrl: (responseId: string) => `/v1/responses/${responseId}.pdf`,
  },

  embeds: {
    list: (formId: string) =>
      request<{ data: EmbedConfigDto[] }>('GET', `/v1/forms/${formId}/embeds`),

    create: (formId: string, input: {
      name?: string; mode?: string; renderMode?: string;
      requireSignedToken?: boolean; origins?: string[];
    }) => request<EmbedConfigDto>('POST', `/v1/forms/${formId}/embeds`, input),

    addOrigin: (embedId: string, origin: string) =>
      request<{ origin: string; alreadyPresent: boolean }>(
        'POST', `/v1/embeds/${embedId}/origins`, { origin }),

    origins: (embedId: string) =>
      request<{ data: { id: number; origin: string; isWildcard: boolean }[] }>(
        'GET', `/v1/embeds/${embedId}/origins`),

    removeOrigin: (embedId: string, originId: number) =>
      request<void>('DELETE', `/v1/embeds/${embedId}/origins/${originId}`),

    mintToken: (embedId: string, input: {
      subject: string; origin: string; claims?: Record<string, string>;
    }) => request<{ token: string; expiresUtc: string; kid: string }>(
      'POST', `/v1/embeds/${embedId}/token`, input),
  },

  team: {
    members: () => request<{ data: TeamMemberDto[]; requireMfa: boolean }>(
      'GET', '/v1/team/members'),

    roles: () => request<{ data: RoleDto[] }>('GET', '/v1/team/roles'),

    changeRole: (membershipId: number, role: string) =>
      request<{ id: number; role: string }>(
        'PATCH', `/v1/team/members/${membershipId}`, { role }),

    remove: (membershipId: number) =>
      request<void>('DELETE', `/v1/team/members/${membershipId}`),

    invitations: () => request<{ data: InvitationDto[] }>('GET', '/v1/team/invitations'),

    invite: (input: { email: string; role: string; companyId?: string }) =>
      request<{
        id: string; email: string; role: string; expiresUtc: string;
        sent: boolean; url?: string | null;
      }>('POST', '/v1/team/invitations', input),

    resendInvitation: (id: string) =>
      request<{ id: string; sent: boolean; url?: string | null }>(
        'POST', `/v1/team/invitations/${id}/resend`),

    revokeInvitation: (id: string) =>
      request<void>('DELETE', `/v1/team/invitations/${id}`),

    setMfaPolicy: (requireMfa: boolean) =>
      request<{ requireMfa: boolean; membersWithoutMfa: number }>(
        'PUT', '/v1/team/mfa-policy', { requireMfa }),
  },

  workspaces: {
    list: (companyId?: string) => {
      const qs = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
      return request<{ data: WorkspaceDto[] }>('GET', `/v1/workspaces${qs}`);
    },

    create: (input: { name: string; companyId?: string }) =>
      request<WorkspaceDto>('POST', '/v1/workspaces', input),

    update: (id: string, input: { name?: string; companyId?: string }) =>
      request<{ id: string; name: string }>('PATCH', `/v1/workspaces/${id}`, input),

    // Refused by the server when the workspace is a default or still holds forms, so the
    // caller can offer it freely and let the message explain.
    remove: (id: string) => request<void>('DELETE', `/v1/workspaces/${id}`),
  },

  account: {
    tenants: () => request<{ data: TenantMembershipDto[] }>('GET', '/v1/auth/tenants'),

    // Returns a full auth response for the target workspace, so the caller must store it
    // the same way it stores a login.
    switchTenant: (tenantId: string) =>
      request<AuthResponse>('POST', '/v1/auth/switch', { tenantId }),

    resendVerification: () =>
      request<{ sent: boolean; alreadyVerified: boolean }>(
        'POST', '/v1/auth/resend-verification'),

    mfa: () => request<MfaStatusDto>('GET', '/v1/account/mfa'),

    enrolMfa: () => request<{
      otpauthUri: string; manualKey: string; digits: number; periodSeconds: number;
    }>('POST', '/v1/account/mfa/enrol'),

    confirmMfa: (code: string) => request<{
      enabled: boolean; recoveryCodes: string[]; warning: string;
    }>('POST', '/v1/account/mfa/confirm', { code }),

    disableMfa: (code: string) =>
      request<{ enabled: boolean }>('POST', '/v1/account/mfa/disable', { code }),

    regenerateRecoveryCodes: (code: string) =>
      request<{ recoveryCodes: string[]; warning: string }>(
        'POST', '/v1/account/mfa/recovery-codes', { code }),
  },

  invitations: {
    preview: (token: string) => request<{
      email: string; workspaceName: string; role: string;
      invitedBy?: string; expiresUtc: string;
    }>('GET', `/v1/invitations/preview?token=${encodeURIComponent(token)}`),

    accept: (token: string) => request<{
      joined: boolean; tenantId: string; workspaceName: string; note: string;
    }>('POST', '/v1/invitations/accept', { token }),
  },

  system: {
    captcha: () => request<{
      provider: string; configured: boolean; siteKeyPreview: string;
    }>('GET', '/v1/system/captcha'),

    email: () => request<{
      provider: string; configured: boolean; fromAddress: string;
      maxRecipientsPerSend: number; restrictedToDomains: string[];
    }>('GET', '/v1/system/email'),
  },

  links: {
    list: (formId: string) =>
      request<{ data: ShareLinkDto[] }>('GET', `/v1/forms/${formId}/links`),

    create: (formId: string, input: {
      name?: string; slug?: string; password?: string;
      opensUtc?: string; closesUtc?: string; maxResponses?: number;
    }) => request<ShareLinkDto>('POST', `/v1/forms/${formId}/links`, input),

    update: (linkId: string, input: {
      isActive?: boolean; password?: string; removePassword?: boolean;
      opensUtc?: string; closesUtc?: string; maxResponses?: number;
    }) => request<ShareLinkDto>('PATCH', `/v1/links/${linkId}`, input),

    remove: (linkId: string) => request<void>('DELETE', `/v1/links/${linkId}`),
  },

  companies: {
    list: () => request<{ data: CompanyDto[] }>('GET', '/v1/companies'),

    create: (input: {
      name: string; legalName?: string; registrationNumber?: string;
      taxId?: string; contactEmail?: string; slug?: string;
    }) => request<{ id: string; name: string; slug: string; defaultWorkspaceId: string }>(
      'POST', '/v1/companies', input),

    workspaces: (companyId: string) =>
      request<{ data: { id: string; name: string; slug: string; isDefault: boolean }[] }>(
        'GET', `/v1/companies/${companyId}/workspaces`),

    update: (companyId: string, input: {
      name?: string; legalName?: string; registrationNumber?: string;
      taxId?: string; contactEmail?: string;
    }) => request<{ id: string; name: string }>('PATCH', `/v1/companies/${companyId}`, input),

    /**
     * Archives rather than deletes. The forms and responses underneath stay reachable for
     * export and audit — which is why the button says "Archive" and not "Delete".
     */
    archive: (companyId: string) =>
      request<void>('DELETE', `/v1/companies/${companyId}`),
  },

  /** The account's own name, logo and contact details, inherited by every form. */
  organisation: {
    get: () => request<OrganisationDto>('GET', '/v1/organisation'),
    update: (input: OrganisationBranding) =>
      request<{ branding: OrganisationBranding }>('PUT', '/v1/organisation', input),
  },

  notifications: {
    list: (limit = 30) =>
      request<{ data: NotificationDto[]; unreadCount: number }>(
        'GET', `/v1/notifications?limit=${limit}`),

    // Polled on an interval. Deliberately its own endpoint returning a single integer:
    // polling the full list every 30 seconds would be far more traffic for one number.
    unreadCount: () => request<{ unreadCount: number }>('GET', '/v1/notifications/unread-count'),

    markRead: (id: string) => request<void>('POST', `/v1/notifications/${id}/read`),

    markAllRead: () => request<{ marked: number }>('POST', '/v1/notifications/read-all'),

    pushKey: () => request<{ publicKey: string | null }>('GET', '/v1/notifications/push/key'),

    subscribe: (input: { endpoint: string; p256dh: string; auth: string }) =>
      request<{ id: string }>('POST', '/v1/notifications/push/subscribe', input),
  },

  audit: {
    list: (params?: {
      action?: string; entityType?: string; limit?: number; cursor?: string;
    }) => {
      const search = new URLSearchParams();
      if (params?.action) search.set('action', params.action);
      if (params?.entityType) search.set('entityType', params.entityType);
      search.set('limit', String(params?.limit ?? 100));

      // Only the first page of a search is audited server-side, so paging costs nothing
      // in the log a reader is looking at.
      if (params?.cursor) search.set('cursor', params.cursor);

      return request<AuditPage>('GET', `/v1/audit-logs?${search}`);
    },
  },

  invites: {
    list: (formId: string, status?: string) => {
      const qs = status ? `?status=${encodeURIComponent(status)}` : '';
      return request<{ data?: never; invites: InviteDto[]; summary: Record<string, number> }>(
        'GET', `/v1/forms/${formId}/invites${qs}`);
    },

    // The tokens in this response exist nowhere else. Whatever calls this is the last
    // chance to capture them.
    issue: (formId: string, input: {
      recipients?: { email?: string; ref?: string; prefill?: Record<string, unknown> }[];
      count?: number;
      expiresUtc?: string;
      maxUses?: number;
      baseUrl?: string;
    }) => request<{
      issued: number;
      warning: string;
      invites: { id: string; url: string; expiresUtc?: string }[];
    }>('POST', `/v1/forms/${formId}/invites`, input),

    revoke: (formId: string, inviteIds: string[]) =>
      request<{ revoked: number; requested: number }>(
        'POST', `/v1/forms/${formId}/invites/revoke`, { inviteIds }),
  },

  projects: {
    list: (workspaceId?: string) => {
      const qs = workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : '';
      return request<{ data: ProjectDto[] }>('GET', `/v1/projects${qs}`);
    },

    create: (input: { name: string; workspaceId?: string; parentId?: string; color?: string }) =>
      request<ProjectDto>('POST', '/v1/projects', input),

    update: (projectId: string, input: { name?: string; color?: string; parentId?: string }) =>
      request<ProjectDto>('PATCH', `/v1/projects/${projectId}`, input),

    // Forms come out rather than going with it, which the confirmation says before asking.
    remove: (projectId: string) =>
      request<{ id: string; formsReleased: number; foldersPromoted: number }>(
        'DELETE', `/v1/projects/${projectId}`),

    file: (formId: string, projectId: string | null) =>
      request<{ formId: string; projectId: string | null }>(
        'PUT', `/v1/forms/${formId}/project`, { projectId }),
  },

  translations: {
    // Read-only: the key list is the server's, and translations are written back through
    // the ordinary definition save so they get the same autosave and undo as any edit.
    get: (formId: string) =>
      request<TranslationsDto>('GET', `/v1/forms/${formId}/translations`),

    // Returns suggestions rather than saving them, so machine output goes through the same
    // undo stack as anything else the author does.
    auto: (formId: string, locale: string, retranslate = false) =>
      request<AutoTranslateDto>(
        'POST', `/v1/forms/${formId}/translations/${locale}/auto`, { retranslate }),
  },

  themes: {
    list: () => request<{ data: ThemeDto[] }>('GET', '/v1/themes'),
    get: (themeId: string) => request<ThemeDto>('GET', `/v1/themes/${themeId}`),

    create: (input: { name: string; tokens: ThemeTokensDto; isDefault?: boolean }) =>
      request<ThemeDto>('POST', '/v1/themes', input),

    update: (themeId: string, input: { name: string; tokens: ThemeTokensDto; isDefault?: boolean }) =>
      request<ThemeDto>('PUT', `/v1/themes/${themeId}`, input),

    remove: (themeId: string) => request<void>('DELETE', `/v1/themes/${themeId}`),

    // Validates and measures without storing. Called while an author is still dragging a
    // colour picker, so it must stay cheap and must never mutate anything.
    preview: (input: { name: string; tokens: ThemeTokensDto }) =>
      request<ThemePreviewDto>('POST', '/v1/themes/preview', input),

    assign: (formId: string, themeId: string | null) =>
      request<{ formId: string; themeId: string | null }>(
        'PUT', `/v1/forms/${formId}/theme`, { themeId }),
  },

  sessions: {
    list: () => request<{ data: SessionDto[] }>('GET', '/v1/auth/sessions'),

    revoke: (sessionId: string) =>
      request<{ revoked: number; sessionId: string }>(
        'DELETE', `/v1/auth/sessions/${sessionId}`),

    // Keeps the caller signed in unless asked otherwise: somebody who has just spotted a
    // session they do not recognise still needs to be in the account to change a password.
    revokeAll: (includeCurrent = false) =>
      request<{ sessions: number; tokens: number; keptCurrent: boolean }>(
        'POST', '/v1/auth/sessions/revoke-all', { includeCurrent }),
  },

  apiKeys: {
    list: () => request<{ data: ApiKeyDto[] }>('GET', '/v1/api-keys'),

    scopes: () => request<{ data: { scope: string; available: boolean }[] }>(
      'GET', '/v1/api-keys/scopes'),

    // The response to this call is the only place the key will ever exist. Whatever calls
    // it has to show the value before doing anything else that could fail.
    create: (input: {
      name: string;
      scopes: string[];
      environment?: string;
      expiresUtc?: string | null;
      allowedIps?: string[];
    }) => request<ApiKeyCreatedDto>('POST', '/v1/api-keys', input),

    revoke: (keyId: string) =>
      request<{ id: string; revokedUtc: string; alreadyRevoked: boolean }>(
        'DELETE', `/v1/api-keys/${keyId}`),
  },

  analytics: {
    status: (formId: string) =>
      request<RollupStatusDto>('GET', `/v1/forms/${formId}/analytics/status`),

    rollup: (formId: string) =>
      request<{ formId: string; rowsProcessed: number; complete: boolean }>(
        'POST', `/v1/forms/${formId}/analytics/rollup`, {}),

    rebuild: (formId: string) =>
      request<{ formId: string; rowsProcessed: number; passes: number }>(
        'POST', `/v1/forms/${formId}/analytics/rebuild`, {}),
  },
};


/** Downloads go through a real anchor so the browser handles Content-Disposition. */
export async function downloadCsv(formId: string, filename: string) {
  const auth = authStore.read();
  const res = await fetch(api.responses.csvUrl(formId), {
    headers: auth ? { Authorization: `Bearer ${auth.accessToken}` } : {},
  });
  if (!res.ok) throw new ApiError(res.status, 'export_failed', 'Export failed.');

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
